import { supabase } from './supabase';

interface UserActivity {
  user_id?: string;
  session_id: string;
  page_url: string;
  referrer?: string;
  time_spent?: number;
  scroll_depth?: number;
  device_info: {
    userAgent: string;
    platform: string;
    language: string;
    screenSize: string;
    connection?: string;
    deviceMemory?: number;
    hardwareConcurrency?: number;
  };
  interaction_type: 'page_view' | 'content_view' | 'download' | 'form_start' | 'form_complete' | 'social_share' | 'search' | 'filter' | 'sort' | 'print' | 'copy' | 'bookmark';
  content_id?: string;
  metadata?: Record<string, any>;
  performance_metrics?: {
    ttfb?: number;
    fcp?: number;
    lcp?: number;
    cls?: number;
    fid?: number;
  };
  conversion_data?: {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
    conversion_value?: number;
  };
}

interface ShareAnalytics {
  contentId: string;
  platform: string;
  referrer?: string;
}

class Analytics {
  private sessionId: string;
  private pageLoadTime: number;
  private maxScrollDepth: number;
  private activityBuffer: UserActivity[];
  private flushInterval: number;
  private isProcessing: boolean;
  private performanceMetrics: {
    ttfb?: number;
    fcp?: number;
    lcp?: number;
    cls?: number;
    fid?: number;
  };

  constructor() {
    this.sessionId = crypto.randomUUID();
    this.pageLoadTime = Date.now();
    this.maxScrollDepth = 0;
    this.activityBuffer = [];
    this.flushInterval = 10000; // 10 seconds
    this.isProcessing = false;
    this.performanceMetrics = {};

    this.trackScroll();
    this.startPeriodicFlush();
    this.trackPerformance();
  }

  private trackScroll() {
    window.addEventListener('scroll', () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = (window.scrollY / scrollHeight) * 100;
      this.maxScrollDepth = Math.max(this.maxScrollDepth, Math.round(scrolled));
    });
  }

  private trackPerformance() {
    if ('performance' in window) {
      // Time to First Byte
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      this.performanceMetrics.ttfb = navigation.responseStart - navigation.requestStart;

      // First Contentful Paint
      const paint = performance.getEntriesByType('paint');
      const fcp = paint.find(entry => entry.name === 'first-contentful-paint');
      if (fcp) this.performanceMetrics.fcp = fcp.startTime;

      // Largest Contentful Paint
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.performanceMetrics.lcp = lastEntry.startTime;
      }).observe({ entryTypes: ['largest-contentful-paint'] });

      // Cumulative Layout Shift
      new PerformanceObserver((entryList) => {
        let cumulativeScore = 0;
        for (const entry of entryList.getEntries()) {
          cumulativeScore += (entry as any).value;
        }
        this.performanceMetrics.cls = cumulativeScore;
      }).observe({ entryTypes: ['layout-shift'] });

      // First Input Delay
      new PerformanceObserver((entryList) => {
        const firstInput = entryList.getEntries()[0];
        this.performanceMetrics.fid = firstInput.processingStart - firstInput.startTime;
      }).observe({ entryTypes: ['first-input'] });
    }
  }

  private startPeriodicFlush() {
    setInterval(() => this.flushBuffer(), this.flushInterval);
  }

  private async flushBuffer() {
    if (this.isProcessing || this.activityBuffer.length === 0) return;

    this.isProcessing = true;
    const activities = [...this.activityBuffer];
    this.activityBuffer = [];

    try {
      const { error } = await supabase
        .from('user_activities')
        .insert(activities);

      if (error) throw error;
    } catch (error) {
      console.error('Error flushing analytics:', error);
      this.activityBuffer = [...activities, ...this.activityBuffer];
    } finally {
      this.isProcessing = false;
    }
  }

  private getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      connection: (navigator as any).connection?.effectiveType,
      deviceMemory: (navigator as any).deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency
    };
  }

  private getConversionData(): Record<string, string> {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      source: urlParams.get('utm_source') || '',
      medium: urlParams.get('utm_medium') || '',
      campaign: urlParams.get('utm_campaign') || '',
      term: urlParams.get('utm_term') || '',
      content: urlParams.get('utm_content') || ''
    };
  }

  async trackPageView(url: string) {
    const activity: UserActivity = {
      session_id: this.sessionId,
      page_url: url,
      referrer: document.referrer,
      device_info: this.getDeviceInfo(),
      interaction_type: 'page_view',
      metadata: {
        title: document.title,
        performance: this.performanceMetrics
      },
      conversion_data: this.getConversionData()
    };

    this.activityBuffer.push(activity);
  }

  async trackContentView(contentId: string, metadata?: Record<string, any>) {
    const activity: UserActivity = {
      session_id: this.sessionId,
      page_url: window.location.href,
      device_info: this.getDeviceInfo(),
      interaction_type: 'content_view',
      content_id: contentId,
      metadata: {
        ...metadata,
        performance: this.performanceMetrics
      }
    };

    this.activityBuffer.push(activity);
  }

  async trackFormInteraction(type: 'form_start' | 'form_complete', formId: string, metadata?: Record<string, any>) {
    const activity: UserActivity = {
      session_id: this.sessionId,
      page_url: window.location.href,
      device_info: this.getDeviceInfo(),
      interaction_type: type,
      metadata: {
        formId,
        ...metadata,
        performance: this.performanceMetrics
      }
    };

    this.activityBuffer.push(activity);
  }

  async trackSearch(query: string, results: number, filters?: Record<string, any>) {
    const activity: UserActivity = {
      session_id: this.sessionId,
      page_url: window.location.href,
      device_info: this.getDeviceInfo(),
      interaction_type: 'search',
      metadata: {
        query,
        results,
        filters,
        performance: this.performanceMetrics
      }
    };

    this.activityBuffer.push(activity);
  }

  async trackDownload(resourceId: string, format: string) {
    const activity: UserActivity = {
      session_id: this.sessionId,
      page_url: window.location.href,
      device_info: this.getDeviceInfo(),
      interaction_type: 'download',
      content_id: resourceId,
      metadata: {
        format,
        performance: this.performanceMetrics
      }
    };

    this.activityBuffer.push(activity);
  }

  async trackShare({ contentId, platform, referrer }: ShareAnalytics) {
    try {
      // Record share analytics
      const { error: shareError } = await supabase
        .from('share_analytics')
        .insert({
          content_id: contentId,
          share_platform: platform,
          shared_by_ip: null, // For privacy
          user_agent: navigator.userAgent,
          referrer: referrer || document.referrer
        });

      if (shareError) throw shareError;

      // Record user activity
      const activity: UserActivity = {
        session_id: this.sessionId,
        page_url: window.location.href,
        device_info: this.getDeviceInfo(),
        interaction_type: 'social_share',
        content_id: contentId,
        metadata: {
          platform,
          performance: this.performanceMetrics
        }
      };

      this.activityBuffer.push(activity);
    } catch (error) {
      console.error('Error tracking share:', error);
    }
  }

  getSessionDuration(): number {
    return Date.now() - this.pageLoadTime;
  }

  getScrollDepth(): number {
    return this.maxScrollDepth;
  }

  getPerformanceMetrics() {
    return this.performanceMetrics;
  }
}

export const analytics = new Analytics();